import React, { useEffect, useState } from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import Loader from "../utils/Loader";
import { useUser } from "../contexts/UserContext";

const PrivateRoute = ({ children, requiredPermission }) => {
  const { user, userData, loading, userDataReady } = useUser();
  const { group, type } = useParams();
  const location = useLocation();
  const [forceShow, setForceShow] = useState(false);

  // Determine if the app is embedded in an iframe
  const isEmbedded = window.self !== window.top;

  // Helper function to check a specific permission
  const checkPermission = (permissionPath) => {
    if (!userData?.permissions?.invoiceSystem) {
      return false;
    }

    const permissions = userData.permissions.invoiceSystem;
    const pathArray = permissionPath.split(".");

    let current = permissions;
    for (const path of pathArray) {
      if (!current || current[path] === undefined || current[path] === null) {
        return false;
      }
      current = current[path];
    }

    const result = current === true;
    return result;
  };

  // Helper function to check multiple permissions (OR logic)
  const checkAnyPermission = (permissions) => {
    for (const permission of permissions) {
      if (checkPermission(permission)) {
        return true;
      }
    }
    return false;
  };

  // Permission mapping for dynamic routes
  const getPermissionFromRoute = () => {
    // Handle dynamic /:group/:type routes
    if (group && type) {
      const routePermissionMap = {
        // Receipts group
        receipts: {
          invoices: ["receipts.invoicesSubAccess"],
          noc: ["receipts.noticeChargesAccess"],
          acknowledgement: ["receipts.ackReceiptsAccess"],
        },
        // Records group
        records: {
          // External records
          transmittal: ["records.external.transmittalAccess"],
          "legal-advice": ["records.external.legalAdviceAccess"],
          "sec-res": ["records.external.secretariatResolutionsAccess"],
          // Internal records
          "case-brief": ["records.internal.caseBriefAccess"],
          "exec-admin": ["records.internal.executiveAdminAccess"],
          "secretarial-records": ["records.internal.secretarialLogsAccess"],
          "process-server-logs": ["records.internal.processServerLogsAccess"],
        },
      };

      const permissions = routePermissionMap[group]?.[type];
      return permissions;
    }

    // Handle static routes with multiple permission options
    const staticRoutePermissions = {
      "/invoice-form": [
        "receipts.invoicesSubAccess",
        "receipts.noticeChargesAccess",
      ],
      "/new-doc": [
        "receipts.ackReceiptsAccess",
        "records.external.transmittalAccess",
      ],
      "/edit-doc": [
        "receipts.ackReceiptsAccess",
        "records.external.transmittalAccess",
      ],
    };

    // Check if current path matches any static route (including with parameters)
    const basePath = location.pathname.split("/").slice(0, 2).join("/");
    const permissions =
      staticRoutePermissions[basePath] ||
      staticRoutePermissions[location.pathname];
    return permissions;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setForceShow(true);
    }, 5000); // Force show after 5 seconds

    return () => clearTimeout(timer);
  }, []);

  // While Firebase is checking the auth state or user data is loading, don't render anything
  if ((loading || !userDataReady) && !forceShow) {
    return (
      <div className="flex h-full w-full bg-tier-primary dark:bg-tier-primary-dark">
        <Loader text={"Please wait..."} />
      </div>
    );
  }

  // If no user, redirect:
  // - If embedded in an iframe, redirect to home page
  // - Otherwise, redirect to login page
  if (!user) {
    const redirectPath = isEmbedded ? "/" : "/login";
    return <Navigate to={redirectPath} />;
  }

  // Determine which permissions to check
  const permissionsToCheck = requiredPermission
    ? [requiredPermission]
    : getPermissionFromRoute();

  // If permissions are required, check them
  if (permissionsToCheck && permissionsToCheck.length > 0) {
    const hasPermission = checkAnyPermission(permissionsToCheck);

    if (!hasPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

export default PrivateRoute;
